import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import UnsupportedBrowser from '../../assets/images/unsupported-browser.png';

import './MaintenancePage.scss';

interface Props extends WrappedComponentProps {
  title?: string;
  content?: string;
  isIE?: boolean;
}

const MaintenancePage = ({
  title,
  content,
  isIE,
  intl,
}: Props): React.ReactElement => {
  title = title || 'maintenance.title';
  content = content || 'maintenance.content';

  document.title = intl.formatMessage({
    id: 'page_title.other',
    defaultMessage: 'Quipper',
  });

  const getIllustration = (): React.ReactElement => {
    if (isIE) {
      return <img src={UnsupportedBrowser} alt={'Unsupported browser'} />;
    }

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        width="120"
        height="120"
        viewBox="0 0 120 120"
      >
        <defs>
          <path id="a" d="M0 120h120V0H0z" />
        </defs>
        <g fill="none" fillRule="evenodd">
          <path
            fill="#E3ECF5"
            d="M120 60c0 33.138-26.862 60-60 60-33.138 0-60-26.862-60-60C0 26.862 26.862 0 60 0c33.138 0 60 26.862 60 60"
          />
          <mask id="b" fill="#fff">
            <use xlinkHref="#a" />
          </mask>
          <path
            fill="#00BEFF"
            d="M33 99h54v-6H33zM45 75h30V63H45zM51 51h18V39H51z"
            mask="url(#b)"
          />
          <path
            fill="#0056B3"
            d="M36 93h48c1.653 0 3 1.347 3 3v3H33v-3c0-1.653 1.347-3 3-3zm24.657-72l16.5 66H42.84l16.5-66h1.317zM84 87h-.66L65.91 17.271c-.021-.087-.081-.156-.108-.243a3.104 3.104 0 0 0-.165-.381 3.173 3.173 0 0 0-.393-.579c-.081-.093-.156-.186-.246-.27a3.067 3.067 0 0 0-.627-.423c-.099-.051-.183-.114-.285-.153A2.937 2.937 0 0 0 63 15h-6c-.384 0-.747.087-1.089.222-.099.039-.183.099-.276.15-.228.12-.447.255-.636.432-.09.078-.162.168-.237.258a2.866 2.866 0 0 0-.564.96c-.027.087-.087.159-.111.249L36.657 87H36c-4.968 0-9 4.029-9 9v6a3 3 0 0 0 3 3h60a3 3 0 0 0 3-3v-6c0-4.971-4.032-9-9-9z"
            mask="url(#b)"
          />
        </g>
      </svg>
    );
  };

  return (
    <div className="maintenance">
      <figure className={`maintenance__illust ${isIE ? 'with-ie' : ''}`}>
        {getIllustration()}
      </figure>
      <h1 className="maintenance__title">
        <FormattedMessage
          id={title}
          defaultMessage={'Our payment system is under maintenance.'}
        />
      </h1>
      <p className="maintenance__text">
        <FormattedMessage
          id={content}
          defaultMessage={
            "We're sorry for the inconvenience. Our payment system is under maintenance and will be back shortly. Thank you."
          }
        />
      </p>
      <small className="maintenance__logo">
        <svg
          height="32"
          viewBox="0 0 129 32"
          width="129"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m45.16 9.371v11.786h1.569v4.27h-6.051l-.865-2.022c-.704.802-1.825 2.472-4.546 2.472-4.834 0-6.435-2.505-6.435-7.835v-4.432h-1.344v-4.239h7.747v7.74c0 2.76.416 4.046 1.729 4.046 1.12 0 2.017-.996 2.017-2.441v-5.106h-2.113v-4.239zm7.722-1.834c-1.986 0-3.404-1.675-3.404-3.193 0-1.455 1.198-3.226 3.404-3.226 2.175 0 3.468 1.771 3.468 3.226 0 1.74-1.23 3.193-3.468 3.193zm2.892 1.834v11.843h1.492v4.204h-8.706v-4.204h1.306v-7.67h-1.306v-4.174h7.213zm12.79 12.109c-1.214 0-2.3-1.668-2.3-3.848-.127-2.533 1.023-4.105 2.268-4.105 1.406 0 2.3 2.02 2.3 3.945 0 2.437-.926 4.008-2.268 4.008m1.598-12.474c-1.917 0-3.259.93-3.866 1.379l-.543-1.122h-7.316v4.264h1.342v14.11h-1.342v4.2h9.553v-4.2h-1.726v-3.24c.223.193 1.502 1.251 4.153 1.251 4.122 0 7.284-3.142 7.284-8.24 0-4.554-2.556-8.402-7.54-8.402m18.28 12.474c-1.203 0-2.28-1.668-2.28-3.848-.126-2.533 1.014-4.105 2.25-4.105 1.392 0 2.279 2.02 2.279 3.945 0 2.437-.919 4.008-2.248 4.008m1.583-12.474c-1.9 0-3.23.93-3.831 1.379l-.539-1.122h-7.25v4.264h1.33v14.11h-1.33v4.2h9.467v-4.2h-1.71v-3.24c.221.193 1.488 1.251 4.116 1.251 4.085 0 7.22-3.142 7.22-8.24 0-4.554-2.534-8.402-7.473-8.402m17.024 4.212c2.021 0 2.306 1.339 2.338 2.55h-4.771c.063-.797.505-2.55 2.433-2.55m1.321 8.323c1.075 0 2.717-.096 4.424-1.532l2.654 3.094c-2.338 2.04-4.866 2.774-8.152 2.774-3.917 0-8.34-2.52-8.34-8.641 0-5.23 3.728-8.323 8.498-8.323 3.76 0 6.635 1.945 7.552 4.624.379 1.116.726 3.57.062 5.389h-9.952c.19.86.6 2.615 3.254 2.615zm20.63-7.272c-2.897 0-4.539.727-4.539 3.512v3.259h1.61v4.144h-9.11v-4.144h1.417v-7.562h-1.385v-4.113h6.052l.965 2.025c.837-1.519 1.675-2.816 4.99-2.816zm-116.788 6.1c-2.378 0-4.307-3.338-4.307-7.456s1.929-7.455 4.307-7.455c2.38 0 4.309 3.337 4.309 7.455s-1.93 7.456-4.309 7.456zm11.52 2.567c0 .614-.072 1.627-.72 1.627-.9 0-1.297-1.23-1.585-2.35 1.981-2.17 2.99-5.242 2.99-9.037-.001-7.699-3.819-12.831-12.317-12.831-7.239 0-12.1 5.204-12.1 12.578 0 8.53 5.042 12.796 12.316 12.796.972 0 1.945-.073 2.81-.217.792 2.132 2.628 4.012 5.581 4.012 3.602 0 5.402-2.566 5.402-5.783v-1.844h-2.377z"
            fill="#00beff"
            fillRule="evenodd"
          />
        </svg>
      </small>
    </div>
  );
};

export default injectIntl(MaintenancePage);
